import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { TennisBall, CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function ResetPassword() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-bg min-h-screen flex items-center justify-center px-4 py-10" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-md bg-[#dfff00] flex items-center justify-center">
            <TennisBall size={22} weight="duotone" color="#09090b" />
          </div>
          <div className="font-heading uppercase tracking-tight text-2xl leading-none flex items-baseline">
            <span className="text-white">courtside</span><span className="text-[#dfff00] ml-0.5">OS</span>
          </div>
        </div>

        <div className="surface p-7">
          <h1 className="font-heading uppercase text-3xl mb-1">New password</h1>
          {done ? (
            <div data-testid="reset-success">
              <div className="flex items-start gap-3 mt-4 bg-[#16ec06]/10 border border-[#16ec06]/30 rounded-md p-4">
                <CheckCircle size={20} weight="fill" className="text-[#16ec06] mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300">Password updated. You can now sign in with your new password.</p>
              </div>
              <Link to="/login" className="btn-primary w-full mt-5" data-testid="reset-go-login">
                Sign in <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          ) : (
            <>
              <p className="text-zinc-400 text-sm mb-6">Choose a new password for your account.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                <div>
                  <label className="font-label block mb-1.5">New password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    className="input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="font-label block mb-1.5">Confirm password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    className="input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm-input"
                    autoComplete="new-password"
                  />
                </div>
                {err && <div className="text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-3" data-testid="reset-error">{err}</div>}
                <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="reset-submit">
                  {busy ? "Saving…" : "Set new password"}
                </button>
              </form>
              <p className="text-xs text-zinc-500 mt-4">
                Link expired? <Link to="/forgot-password" className="text-[#dfff00] hover:underline" data-testid="reset-request-new">Request a new one</Link>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
